// The rail's half of a gesture's live frame (SPEC-viewfinder §4.1), and the
// FLIP the settle earns (§4.2).
//
// Under the finger exactly two things change on the list: the distance on a row
// that is already there, and whether a row is there at all. **It never
// re-orders** — „nieposortowane traci dla mnie sens, ale z drugiej strony live
// też bym chciał mieć", so the ruling is live membership, settled order — and
// it never rebuilds: a rebuild would drop the scroll, the focus and the anchor
// sixty times a second, and a data poll that lands mid-gesture must rebuild
// nothing either (§4.1).
//
// So this writes text into cells that exist and hides rows that have left the
// ring. Every one of those changes is undone by the ONE rebuild on settle.
import { distance } from '../derive/axis.ts';
import { gesture, type LiveMeasure } from '../state/gesture.ts';
import type { Order } from '../state/viewstate.ts';

/** How long a settle's re-order may take, and §4.2's own ceiling. */
const FLIP_MS = 200;

/**
 * One frame: the distances, and membership. Both read off the row's own
 * `data-id`, so a run, a note, a gap and the tick — which have no id and no
 * distance — are left exactly as they are.
 */
/**
 * Where each row sits in the rail's own content, taken ONCE per gesture.
 *
 * A distance nobody can see is not worth a text write under a finger — the
 * rail is 59 rows on a busy afternoon and about eight of them are on a phone's
 * screen. But asking every row for its `offsetTop` on every measurement was
 * itself the expensive half: 59 forced layout reads seven times a second, and
 * at 6× CPU that is most of what the review measured as this session's frames
 * over 32 ms.
 *
 * Rows do not move while a gesture is in flight — nothing is added or removed,
 * because the rail does not rebuild under the finger (§4.1) — so the offsets
 * are read on the first measurement and reused until the settle clears them.
 * A `hidden` row does shift the ones below it; that only changes which rows are
 * chosen for a re-spell, never what any of them says, and the settle's rebuild
 * re-spells all of them anyway.
 */
let offsets: { rows: HTMLElement[]; tops: number[] } | null = null;

export function applyLive(list: HTMLElement, live: LiveMeasure, order: Order): void {
  const rows = [...list.querySelectorAll<HTMLElement>('li[data-id]')];
  if (!offsets || offsets.rows.length !== rows.length || offsets.rows[0] !== rows[0]) {
    offsets = { rows, tops: rows.map((row) => row.offsetTop) };
  }
  const box = list.parentElement;
  const top = box ? box.scrollTop : 0;
  const bottom = box ? top + box.clientHeight : Number.POSITIVE_INFINITY;
  const tops = offsets.tops;
  const seen = rows.map((_, i) => (tops[i] ?? 0) >= top - 200 && (tops[i] ?? 0) <= bottom + 200);

  rows.forEach((row, index) => {
    const id = row.dataset.id;
    if (!id) return;
    // The list is the radius (§2.1): a row whose item has left the ring is not
    // on the list in any form. `hidden` rather than a removal, because the
    // settle's rebuild is what really decides the list and this must be exactly
    // undoable — a removed row would take its open detail card with it.
    row.hidden = !live.inside.has(id);
    if (row.hidden || !seen[index]) return;
    const away = live.metres.get(id);
    if (away === undefined) return;
    // Spelled by `derive/`'s own formatter, over `distanceLabel`, so nothing
    // outside `src/shared/geo.ts` spells a distance (§7.4's fitness function).
    const label = distance(away, row.dataset.uncertain === 'true');
    // Walked rather than queried: `rowElement` puts `.row__time` first and its
    // two spans in order, and a `querySelectorAll` per row per frame is a
    // measurable share of a 6× throttled frame.
    const when = row.firstElementChild;
    const into = (order === 'odleglosc' ? when?.firstElementChild : when?.lastElementChild) ?? null;
    if (into && into.textContent !== label) into.textContent = label;
  });
}

/**
 * Puts back what a gesture hid, so the rebuild that follows a settle starts
 * from a list nobody has touched. Called before the rows are replaced.
 */
export function clearLive(list: HTMLElement): void {
  // The gesture is over, so the cached geometry is too: the rebuild that
  // follows makes every one of these elements again.
  offsets = null;
  for (const row of list.querySelectorAll<HTMLElement>('li[hidden]')) row.hidden = false;
}

/**
 * The settle's re-order, animated (§4.2): first, last, invert, play, over
 * `FLIP_MS` and **translate only** — no height, no opacity, nothing that would
 * make a row reflow mid-flight.
 *
 * Visible in distance order alone, because time order does not change under a
 * pan; the caller is what knows that. Under `prefers-reduced-motion: reduce`
 * the rows simply appear in their new places: §8 of SPEC-redesign removes
 * motion rather than shortening it (#233).
 *
 * Positions are read in the SCROLL CONTAINER's own content space —
 * `offsetTop`, not `getBoundingClientRect` — because the rail's scroll is
 * restored in the same pass and a viewport delta would carry that restoration
 * into every row's animation.
 */
export function settleFlip(list: HTMLElement, order: Order): Map<string, number> | null {
  // A settle re-orders a DISTANCE list and nothing else: time order does not
  // change under a pan. The one-shot is consumed either way, exactly as
  // `orderWasAsked()` is, so a later poll can never inherit it.
  const settled = gesture.tookSettle();
  if (!settled || order !== 'odleglosc') return null;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
  return firstPositions(list);
}

function firstPositions(list: HTMLElement): Map<string, number> {
  const was = new Map<string, number>();
  for (const row of list.querySelectorAll<HTMLElement>('li[data-id]')) {
    if (row.dataset.id) was.set(row.dataset.id, row.offsetTop);
  }
  return was;
}

export function playFlip(list: HTMLElement, was: Map<string, number>): void {
  for (const row of list.querySelectorAll<HTMLElement>('li[data-id]')) {
    const id = row.dataset.id;
    const from = id ? was.get(id) : undefined;
    if (from === undefined) continue;
    const delta = from - row.offsetTop;
    // A row that did not move animates nothing: sixty no-op animations a settle
    // is what makes a FLIP cost more than the re-order it dresses.
    if (Math.abs(delta) < 1) continue;
    row.animate([{ transform: `translateY(${delta}px)` }, { transform: 'none' }], {
      duration: FLIP_MS,
      easing: 'cubic-bezier(.2,.7,.3,1)',
    });
  }
}
